import React, { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as z from 'zod';
import { FileCategoryEnum, FileFormatEnum } from 'types/generated/server';
import { VideoAnalysisSkillsTypeEnum } from 'utils/shared/coachBuilder';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useRequestStatus } from 'hooks/useRequestStatus';
import { useUploadVideoFile } from 'hooks/useUploadVideoFile';
import Alert from 'svg/Alert';
import Button from 'components/Button';
import MultiDropzone from 'components/MultiDropzone';

type UploadFailedProps = {
  errorUpload: string | null;
};

export function UploadFailed({ errorUpload }: UploadFailedProps) {
  const { user } = useGetCurrentUser();

  const [showVideoUploader, setShowVideoUploader] = useState<Boolean>(false);
  const { uploadVideoFile, isLoadingUpload } = useUploadVideoFile();
  const userId = user?.id;
  const {
    isLoading: isImageLoading,
    setLoading: setImageLoading,
    setSuccess: setImageSuccess,
    setError: setImageError,
  } = useRequestStatus();

  const userFilesSchema = z.object({
    fileCategory: z.enum([
      FileCategoryEnum.Coach,
      FileCategoryEnum.Other,
      FileCategoryEnum.Profile,
    ]),
    fileName: z.string(),
    fileType: z.string(),
    host: z.string(),
    originalFileName: z.string(),
    path: z.string(),
    provider: z.string(),
    providerUrl: z.string(),
    url: z.string(),
    fileFormat: z.enum([FileFormatEnum.Image, FileFormatEnum.Video]),
    providerId: z.string(),
    userId: z.string(),
  });

  const coachSettingsSchema = z.object({
    //   photo: photoSchema,
    userFiles: z.array(userFilesSchema),
    priceUnitAmountCoachDefault: z.number().max(10000).min(0),
    //   gender: z.enum([GenderEnum.Male, GenderEnum.Female]),
    // venue: venueSchema,
    //   coachAvailability: z.array(refinedDayAvailabilitySchema), //schedule
    aboutMe: z.string(),
    hasPciId: z.boolean().optional(),
    sameScheduleForMultipleDays: z.boolean().optional(),
    // hasPprId: z.boolean().optional(),
    //   ageGroups: z.object(dynamicAgeGroupSchemaObject),
    //   skillLevels: z.object(skillLevelSchemaObject),
    phoneNumber: z
      .string()
      .refine((val) => val.replace(/\D/g, '').length >= 8 && val.replace(/\D/g, '').length <= 15, {
        message: 'Phone number must be between 8 and 15 digits including the country code.',
      }),
    phoneCountryCode: z.string().optional(),
    coachingStartYear: z
      .union([
        z
          .number()
          .int()
          .min(0, { message: 'Value must be at least 0' })
          .max(50, { message: 'Value must be at most 50' }),
        z.nan(),
      ])
      .optional(),
    coachLessonDescription: z.string(),
    coachLessonHowToPrepare: z.string(),
    //   rating: z.object(ratingSchema),
    externalBookingUrl: z.string().optional(),
    defaultSport: z.string(),
    skills_category: z.string(),
  });

  const {
    control,
    formState: { errors },
    getValues,
    watch,
    handleSubmit,
    setValue,
    register,
    setError: setFormError,
    clearErrors: clearFormErrors,
  } = useForm<z.infer<typeof coachSettingsSchema>>({
    resolver: zodResolver(coachSettingsSchema),
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: {
      sameScheduleForMultipleDays: false,
      // coachAvailability: [],
      priceUnitAmountCoachDefault: undefined,
      //   gender: GenderEnum.Male, // Assuming a default gender value
      aboutMe: '',
      externalBookingUrl: '',
      //   ageGroups: defaultAgeGroups,
      //   skillLevels: defaultSkillLevels,
      hasPciId: false,
      // hasPprId: false,
      coachLessonDescription: '',
      coachLessonHowToPrepare: '',
      //   rating: defaultRatingValues,
      phoneNumber: '',
      phoneCountryCode: '',
      defaultSport: '',
      skills_category: VideoAnalysisSkillsTypeEnum.GENERAL_FEEDBACK, // Add this line
    },
  });

  const TryAgainVideo = () => {
    setShowVideoUploader(true);
  };

  return (
    <>
      <div className="flex flex-col items-start gap-8">
        <h6 className="typography-product-heading-compact text-color-text-brand dark:text-color-text-brand">
          Your video failed to upload
        </h6>
        <p>Please try uploading your video again and do not exit the page until it is complete.</p>
      </div>

      {!showVideoUploader && (
        <>
          <div className="my-12 flex flex-col items-center">
            <Alert />
          </div>
          <Button variant="brand" size="lg" label="Try again" onClick={TryAgainVideo} />
        </>
      )}

      {showVideoUploader && (
        <>
          {/* Video Upload */}
          <div className="mb-12 mt-8 flex h-full w-full flex-col gap-4 ">
            <h6 className="typography-product-subheading text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Upload your video
            </h6>
            <Controller
              control={control}
              name="userFiles"
              render={({ field: { onChange, value } }) => {
                return (
                  <MultiDropzone
                    multiple={true}
                    uploading={isLoadingUpload}
                    styles="rounded-md"
                    maxFiles={8}
                    accept={{
                      'image/*': [],
                      'video/*': ['.mp4'], // Accepting video files with .mp4 extension
                    }}
                    onRemoveFile={(_, fileIndex) => {
                      const currentFiles = [...value];
                      currentFiles.splice(fileIndex, 1);
                      setValue('userFiles', currentFiles);
                    }}
                    onSelectFiles={async (files) => {
                      try {
                        setImageLoading();

                        // Filter video files (if needed)
                        const videoFiles = files.filter((file) => file.type.startsWith('video/'));

                        // Upload each video file and track progress
                        const uploadPromises = videoFiles.map((file) => uploadVideoFile({ file }));

                        // Wait for all video files to be uploaded
                        const payloads = await Promise.all(uploadPromises);
                        console.log('payloads in PayloadFailed', payloads);

                        if (!payloads) return;

                        // Process uploaded files and update the state
                        const updatedFiles = payloads.map((payload) => ({
                          fileCategory: FileCategoryEnum.Coach,
                          fileName: payload?.fileName,
                          fileType: payload?.fileType,
                          host: payload?.host,
                          originalFileName: payload?.originalFileName,
                          path: payload?.path,
                          provider: payload?.provider,
                          providerUrl: payload?.providerUrl,
                          fileFormat: payload?.fileType.startsWith('video/')
                            ? FileFormatEnum.Video
                            : FileFormatEnum.Image,
                          url: payload?.url,
                          providerId: '',
                          userId,
                        }));

                        // Update the files in the form state
                        onChange([...value, ...updatedFiles]);

                        setImageSuccess(); // Set success state
                      } catch (error) {
                        console.error('Error uploading files:', error);
                        setImageError(''); // Handle error
                      }
                    }}
                  />
                );
              }}
            />

            {errorUpload && <p className="mt-2 text-xs text-color-error">{errorUpload}</p>}
          </div>
        </>
      )}
    </>
  );
}

export default UploadFailed;
